import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Runs `run` once when the URL carries `?action=<name>`, then removes the param (history is
 * replaced, not pushed). Used by the project menus' deep links (import, custom-fields,
 * new-task, new-phase). While `ready` is false the param is kept, so an action that needs
 * data or a permission waits for it; if it never becomes ready the param stays and nothing
 * runs, rather than opening a control the caller cannot use.
 */
export function useRouteAction(name: string, ready: boolean, run: () => void): void {
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get('action') === name;
  useEffect(() => {
    if (!requested || !ready) return;
    run();
    const next = new URLSearchParams(searchParams);
    next.delete('action');
    setSearchParams(next, { replace: true });
    // `run` is intentionally not a dependency: it only needs to fire once per request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requested, ready]);
}
